import React from "react";
import PersonalInformation from "../Sections/PersonalInformation";
import DiagnosticProfile from "../Sections/DiagnosticProfile";
import Providers from "../Sections/Providers";
import FinancialGuarantor from "../Sections/FinancialGuarantor";
import WeeklyAvailability from "../Sections/TherapyHours/WeeklyAvailability";
import MakeupSessionPreferences from "../Sections/TherapyHours/MakeupSessionPreferences";
import PreliminaryTherapyHours from "../Sections/TherapyHours/PreliminaryTherapyHours";
import Insurance from "../Sections/Insurance";
import EmailLogOnlyPage from "../Sections/Communication/EmailLogOnlyPage";
import Documents from "../Sections/Documents";
import Authorizations from "../Sections/Authorizations";
import Assessments from "../Sections/Assessments";
import Logs from "../Sections/Logs";
import AccountSettings from "../Sections/AccountSettings";
import Dashboard from "../Sections/Dashboard";
import CareTeam from "../Sections/CareTeam";
import CareTeamPageShell from "../Pages/CareTeamPageShell";
import FamilyHistory from "../Sections/FamilyHistory";
import CriminalHistory from "../Sections/CriminalHistory";
import FamilyMedicalHistory from "../Sections/FamilyMedicalHistory";
import FamilyMentalHealthHistory from "../Sections/FamilyMentalHealthHistory";
import SleepProfile from "../Sections/SleepProfile";
import TreatmentVariables from "../Sections/TreatmentVariables";
import DevelopmentalHistory from "../Sections/DevelopmentalHistory";
import SocialActivities from "../Sections/SocialActivities";
import Education from "../Sections/Education";
import MedicalHistory from "../Sections/MedicalHistory";
import DepressionScreening from "../Sections/DepressionScreening";
import TraumaHistory from "../Sections/TraumaHistory";
import Medications from "../Sections/Medications";
import InterventionHistory from "../Sections/InterventionHistory";
import CrisisPlan from "../Sections/CrisisPlan";
import DischargePlanning from "../Sections/DischargePlanning";
import CaregiverInvolvement from "../Sections/CaregiverInvolvement";
import ClinicalApproach from "../Sections/ClinicalApproach";
import TreatmentFocusArea from "../Sections/TreatmentFocusArea";
import ClinicalProgram from "../Sections/ClinicalProgram";
import ObservedBehaviorsAndStrengths from "../Sections/StrengthsAndAreasOfNeed/ObservedBehaviorsAndStrengths";
import CurrentCommunication from "../Sections/StrengthsAndAreasOfNeed/CurrentCommunication";
import AreasOfConcern from "../Sections/StrengthsAndAreasOfNeed/AreasOfConcern";
import PrerequisitesForTelehealth from "../Sections/StrengthsAndAreasOfNeed/PrerequisitesForTelehealth";
import GoalsAndTargetsRouter from "../Sections/GoalsAndTargets/GoalsAndTargetsRouter";

export type PageWidthMode = "full" | "constrained";

/**
 * Route definition supporting two patterns:
 * 1. Single component: Traditional single-section routes (most routes)
 * 2. Sections array: Multi-section pages that compose several sections (e.g., CareTeam page)
 *    When sections are provided, they render in order via PageRenderer with consistent spacing.
 */
export interface ClientDetailsRoute {
  link: string;
  title: string;
  widthMode: PageWidthMode;
  component?: React.ReactNode;
  sections?: React.ReactNode[];
  sectionGap?: number | string;
}

export const clientDetailsRoutes: ClientDetailsRoute[] = [
  {
    link: "dashboard",
    title: "Dashboard",
    widthMode: "full",
    component: <Dashboard />,
  },
  {
    link: "profile",
    title: "Profile",
    widthMode: "constrained",
    component: <PersonalInformation />,
  },
  {
    link: "diagnostic-profile",
    title: "Diagnostic profile",
    widthMode: "constrained",
    component: <DiagnosticProfile />,
  },
  {
    link: "care-team",
    title: "Care team",
    widthMode: "constrained",
    sectionGap: "48px",
    sections: [
      <CareTeamPageShell key="clinicians" />,
      <CareTeam key="care-team" />,
      <Providers key="providers" useSectionWrapper />,
    ],
  },
  {
    link: "insurance",
    title: "Insurance",
    widthMode: "constrained",
    component: <Insurance />,
  },
  {
    link: "financial-guarantor",
    title: "Financial guarantor",
    widthMode: "constrained",
    component: <FinancialGuarantor />,
  },
  {
    link: "authorizations",
    title: "Authorizations",
    widthMode: "constrained",
    component: <Authorizations />,
  },
  {
    link: "weekly-availability",
    title: "Weekly availability",
    widthMode: "constrained",
    component: <WeeklyAvailability />,
  },
  {
    link: "makeup-preferences",
    title: "Make-up preferences",
    widthMode: "constrained",
    component: <MakeupSessionPreferences />,
  },
  {
    link: "preliminary-therapy-hours",
    title: "Preliminary therapy hours",
    widthMode: "constrained",
    component: <PreliminaryTherapyHours />,
  },
  {
    link: "strengths-and-areas-of-need",
    title: "Strengths & Areas of Need",
    widthMode: "constrained",
    sectionGap: "48px",
    sections: [
      <ObservedBehaviorsAndStrengths key="observed-behaviors" />,
      <CurrentCommunication key="current-communication" />,
      <AreasOfConcern key="areas-of-concern" />,
      <PrerequisitesForTelehealth key="prerequisites-telehealth" />,
    ],
  },
  {
    link: "assessments",
    title: "Assessments",
    widthMode: "full",
    component: <Assessments />,
  },
  {
    link: "documents",
    title: "Documents",
    widthMode: "full",
    component: <Documents />,
  },
  {
    link: "communication",
    title: "Communication",
    widthMode: "constrained",
    component: <EmailLogOnlyPage />,
  },
  {
    link: "logs",
    title: "Logs",
    widthMode: "constrained",
    component: <Logs />,
  },
  {
    link: "account-settings",
    title: "Account settings",
    widthMode: "constrained",
    component: <AccountSettings />,
  },
  {
    link: "goals-targets",
    title: "Goals & targets",
    widthMode: "full",
    component: <GoalsAndTargetsRouter />,
  },
  {
    link: "home-family-life",
    title: "Family history",
    widthMode: "constrained",
    sections: [
      <FamilyHistory key="family-history" />,
      <CriminalHistory key="criminal-history" />,
      <FamilyMedicalHistory key="family-medical-history" />,
      <FamilyMentalHealthHistory key="family-mental-health-history" />,
      <SleepProfile key="sleep-profile" />,
      <TreatmentVariables key="treatment-variables" />,
    ],
  },
  {
    link: "developmental-milestones",
    title: "Developmental milestones",
    widthMode: "constrained",
    component: <DevelopmentalHistory />,
  },
  {
    link: "social-activities",
    title: "Social activities",
    widthMode: "constrained",
    component: <SocialActivities />,
  },
  {
    link: "education",
    title: "Education",
    widthMode: "constrained",
    component: <Education />,
  },
  {
    link: "medical-history",
    title: "Medical history",
    widthMode: "constrained",
    sections: [
      <DiagnosticProfile key="diagnostic-profile" />,
      <MedicalHistory key="medical-history" />,
      <DepressionScreening key="depression-screening" />,
      <TraumaHistory key="trauma-history" />,
      <Medications key="medications" />,
      <InterventionHistory key="intervention-history" />,
    ],
  },
  {
    link: "care-plan",
    title: "Care Plan",
    widthMode: "constrained",
    sections: [
      <TreatmentFocusArea key="treatment-focus-area" />,
      <ClinicalApproach key="clinical-approach" />,
      <CaregiverInvolvement key="caregiver-involvement" />,
      <DischargePlanning key="discharge-planning" />,
      <CrisisPlan key="crisis-plan" />,
    ],
  },
  {
    link: "clinical-program",
    title: "Clinical Program",
    widthMode: "constrained",
    component: <ClinicalProgram />,
  },
];

export const getRouteByLink = (link: string): ClientDetailsRoute | undefined => {
  return clientDetailsRoutes.find((route) => route.link === link);
};
